"use client";

/**
 * Three-tab counter strip (spec §6.2.2, Figma `1678:1173`–`1678:1188`).
 *
 * Three large counter cards equal-width inside the recipient list
 * header: No Issues / Warnings / Fix Issues. Each card is a tab trigger
 * that:
 *
 *   - Shows the bucket label, a status icon, and a bold count.
 *   - Highlights when active (`bg-surface-elevated` + 1px brighter
 *     border) so a recruiter can see at a glance which subset of the
 *     cohort is currently rendered in the list below.
 *   - Reads/writes the active bucket from URL search params (`?bucket=`)
 *     so the surface is shareable + survives a refresh per spec §6.2.2.
 *
 * The cards intentionally render even when their count is zero so the
 * strip's column widths stay stable as candidates flip buckets due to
 * an override edit (spec §16.16 race-condition mitigation).
 */

import type { CSSProperties } from "react";

import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

import { BUCKET_META, BUCKET_ORDER } from "../../lib/issue-meta";
import type { PreviewBucket } from "../../lib/types";

export interface BucketCountersProps {
  counts: { no_issue: number; warning: number; fix: number };
  active: PreviewBucket;
  onSelect: (next: PreviewBucket) => void;
}

/** Per-bucket accent (Figma `10351:10363`). The active card gets a subtle
 * top-to-bottom tint of its accent over a flat white/2 wash; inactive
 * cards are a plain white/2 at 50% opacity. Icon uses the bright accent. */
const ACCENT_RGB: Record<PreviewBucket, string> = {
  no_issue: "102, 220, 126",
  warning: "255, 175, 83",
  fix: "239, 68, 68",
};

const ICON_COLOR: Record<PreviewBucket, string> = {
  no_issue: "#66dc7e",
  warning: "#ffaf53",
  fix: "#ef4444",
};

function activeGradient(bucket: PreviewBucket): CSSProperties {
  const rgb = ACCENT_RGB[bucket];
  return {
    backgroundImage: `linear-gradient(180deg, rgba(${rgb}, 0) 0%, rgba(${rgb}, 0.05) 100%), linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.02) 100%)`,
  };
}

export function BucketCounters({
  counts,
  active,
  onSelect,
}: BucketCountersProps) {
  return (
    <div
      role="tablist"
      aria-label="Recipient buckets"
      className="flex gap-3 border-b border-border px-6 py-4 max-lg:gap-2 max-lg:px-4 max-lg:py-3"
    >
      {BUCKET_ORDER.map((bucket) => {
        const meta = BUCKET_META[bucket];
        const count = counts[bucket];
        const isActive = active === bucket;
        return (
          <button
            key={bucket}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={`${meta.label}: ${count}`}
            data-active={isActive}
            onClick={() => onSelect(bucket)}
            style={isActive ? activeGradient(bucket) : undefined}
            className={cn(
              "flex flex-1 flex-col items-start gap-2.5 overflow-clip rounded-lg border border-white/10 p-4 text-left transition-opacity",
              // ARBI-677: compact cards below `lg` so the three-across strip
              // fits a phone without wrapping or clipping the labels.
              "max-lg:gap-1.5 max-lg:p-3",
              isActive ? "opacity-100" : "bg-white/[0.02] opacity-50 hover:opacity-70",
            )}
          >
            <div className="flex items-center gap-2.5 max-lg:gap-1.5">
              <span className="text-base text-foreground/80 max-lg:text-sm">{meta.label}</span>
              <Icon
                name={meta.icon}
                size={20}
                // `size` lands as an inline style, so the responsive shrink
                // needs the trailing-! to win below `lg`.
                className="shrink-0 max-lg:size-4!"
                style={{ color: ICON_COLOR[bucket] }}
              />
            </div>
            <div className="text-2xl font-medium text-white max-lg:text-xl">{count}</div>
          </button>
        );
      })}
    </div>
  );
}
